import React from "react";
import { IoAddOutline, IoTimeOutline } from "react-icons/io5";
import AreaThumbnail from "../areas/AreaThumbnail";
import type { AreaLike } from "../../geo/areaShapes";

const PlusIcon = IoAddOutline as any;
const ClockIcon = IoTimeOutline as any;

export type LandingVariant = "dashboard" | "project" | "area" | "sample";

const TYPE_LABEL: Record<LandingVariant, string> = {
  dashboard: "Dashboard",
  project: "Project",
  area: "Area",
  sample: "Sample",
};

const CARD =
  "relative flex w-full flex-col overflow-hidden rounded-3xl border border-paper-300/70 bg-surface text-left shadow-soft";
const HOVER =
  "group transition-all duration-300 ease-brand hover:-translate-y-1 hover:shadow-card-hover hover:border-brand-500/40";

interface LandingCardProps {
  variant: LandingVariant;
  name: string;
  /** The areas drawn in the thumbnail: the area itself, or a project's members. */
  areas: AreaLike[];
  /** The line under the name. */
  meta?: React.ReactNode;
  /** Marks the dashboard the person had open last. */
  lastViewed?: boolean;
  /** Absent for a sample card, which cannot be opened. */
  onOpen?: () => void;
}

/** One card on the Home landing: an outline thumbnail, a type pill, the name and a line of meta. */
export const LandingCard: React.FC<LandingCardProps> = ({ variant, name, areas, meta, lastViewed, onOpen }) => {
  const sample = variant === "sample";
  const body = (
    <>
      <AreaThumbnail areas={areas} label={`Outline of ${name}`} />
      <div className="p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest ${
              sample ? "bg-note-soft text-note-ink" : "bg-paper-200 text-ink-subtle"
            }`}
          >
            {TYPE_LABEL[variant]}
          </span>
          {lastViewed && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700">
              <ClockIcon className="h-3.5 w-3.5" /> Last viewed
            </span>
          )}
        </div>
        <h3 className="truncate font-display text-lg font-bold text-ink">{name}</h3>
        {meta ? <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-ink-muted">{meta}</p> : null}
      </div>
    </>
  );

  if (sample || !onOpen) {
    return (
      <div role="group" aria-label={name} aria-disabled="true" className={`${CARD} cursor-not-allowed opacity-80`}>
        {body}
      </div>
    );
  }
  return (
    <button type="button" onClick={onOpen} className={`${CARD} ${HOVER}`}>
      {body}
    </button>
  );
};

/** The dashed "+ New ..." card that starts the first item of a section. */
export const LandingNewCard: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex min-h-[10rem] w-full flex-col items-center justify-center gap-2 rounded-3xl border border-dashed border-paper-300 bg-paper-100 p-5 text-ink-muted transition-colors hover:border-brand-500/40 hover:bg-surface hover:text-brand-600"
  >
    <PlusIcon aria-hidden="true" className="h-7 w-7" />
    <span className="text-sm font-semibold">{label}</span>
  </button>
);
